import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FreshnessBadge } from '@/components/Freshness';
import { Header } from '@/components/Header';
import { NavLookupButton } from '@/components/NavLookup';
import { IconExternal } from '@/components/Icons';
import { TradingViewChart } from '@/components/TradingViewChart';
import { AddAssetDialog } from '@/features/addAsset/AddAssetDialog';
import { holdingLabel, useT } from '@/i18n';
import { cx } from '@/lib/cx';
import { formatCompactMoney, formatMoney, formatNumber, formatPct, formatPrice, formatQty, formatRate, formatSigned } from '@/lib/money';
import { formatDateTime, formatShortDate } from '@/lib/time';
import { exchangeLabel, googleFinanceUrl, mutualFundUrl, tradingViewSymbol } from '@/market/exchange';
import { koyfinUrl } from '@/market/koyfin';
import { applyNav, navAcceptable, type NavLookup } from '@/market/navLookup';
import { primeAsset } from '@/market/quoteService';
import { finnhubMetrics, finnhubProfile, finnhubSymbol, type BasicMetrics, type CompanyProfile } from '@/market/finnhub';
import { massiveDailyBars, massiveNews, type DailyBar, type NewsItem } from '@/market/massive';
import { computeTotals, convertFromUsd, valueAsset } from '@/market/value';
import { finnhubKey, massiveKey, useSettings } from '@/settings/settingsStore';
import { appStore, useAppState } from '@/state/appStore';
import { isWatched, quoteKey } from '@/types';
import s from './detail.module.css';

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
}

function returnSince(bars: DailyBar[], days: number, last: number): number | null {
  const cutoff = Date.now() - days * 86_400_000;
  const bar = bars.find((b) => b.t >= cutoff);
  return bar ? ((last - bar.c) / bar.c) * 100 : null;
}

const PERIODS = [['detail.period1m', 30], ['detail.period3m', 91], ['detail.period6m', 182], ['detail.period1y', 365]] as const;

export function DetailView() {
  const { t } = useT();
  const { assetId = '' } = useParams();
  const app = useAppState();
  const settings = useSettings();
  const navigate = useNavigate();
  const asset = app.assets.find((a) => a.id === assetId);
  const [editing, setEditing] = useState(false);
  const [navResult, setNavResult] = useState<NavLookup | null>(null);
  const [refreshingNav, setRefreshingNav] = useState(false);
  // Koyfin pages are keyed by Koyfin's own id, looked up when the page opens so the button is a plain link by the time it is clicked.
  const [koyfin, setKoyfin] = useState<string | null | 'pending'>('pending');
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [metrics, setMetrics] = useState<BasicMetrics | null>(null);
  const [news, setNews] = useState<NewsItem[] | null>(null);
  const [bars, setBars] = useState<DailyBar[] | null>(null);
  const [sideError, setSideError] = useState<string | null>(null);

  const fh = finnhubKey(settings);
  const mv = massiveKey(settings);
  const isEquity = asset?.assetClass === 'stock' || asset?.assetClass === 'etf';
  const isFund = asset?.assetClass === 'fund';
  const symbol = asset?.symbol.toUpperCase() ?? '';
  const caListing = asset?.market === 'CA';
  const providerSymbol = finnhubSymbol(symbol, asset?.market, asset?.mic);

  useEffect(() => {
    if (!asset || asset.assetClass === 'cash') {
      setKoyfin(null);
      return;
    }
    const controller = new AbortController();
    setKoyfin('pending');
    koyfinUrl(asset, controller.signal).then(setKoyfin).catch((e) => {
      if ((e as Error).name !== 'AbortError') setKoyfin(null);
    });
    return () => controller.abort();
  }, [asset?.id, asset?.symbol, asset?.assetClass, asset?.market]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!asset || !isEquity) return;
    const controller = new AbortController();
    setSideError(null);
    if (fh) {
      finnhubProfile(providerSymbol, fh, controller.signal).then(setProfile).catch(() => setProfile(null));
      finnhubMetrics(providerSymbol, fh, controller.signal).then(setMetrics).catch(() => setMetrics(null));
    }
    if (mv && !caListing) {
      massiveNews(symbol, mv, 8, controller.signal).then(setNews).catch((e: unknown) => setSideError((e as Error).message));
      massiveDailyBars(symbol, isoDaysAgo(370), isoDaysAgo(0), mv, controller.signal).then(setBars).catch(() => setBars(null));
    }
    return () => controller.abort();
  }, [asset?.id, symbol, providerSymbol, caListing, isEquity, fh, mv]);

  const totals = useMemo(() => computeTotals(app.assets, app.quotes, app.fx), [app.assets, app.quotes, app.fx]);

  if (!app.ready) return null;
  if (!asset) return <Navigate to="/" replace />;

  const portfolio = app.portfolios.find((p) => p.id === asset.portfolioId);
  const v = valueAsset(asset, app.quotes, app.fx);
  const ccy = settings.displayCurrency;
  const isCash = asset.assetClass === 'cash';
  const cashCcy = asset.currency.toUpperCase();
  const exchange = asset.mic ?? profile?.exchange ?? asset.exchange;
  const share = totals.totalUsd > 0 && v.valueUsd != null ? (v.valueUsd / totals.totalUsd) * 100 : null;
  const pnlPct = v.costUsd != null && v.costUsd > 0 && v.valueUsd != null ? ((v.valueUsd - v.costUsd) / v.costUsd) * 100 : null;
  const last = v.quote?.price ?? asset.manualPrice ?? null;
  const watched = isWatched(asset);
  const tvSymbol = tradingViewSymbol(symbol, exchange, asset.assetClass);

  const remove = async () => {
    if (!window.confirm(t('detail.confirmDelete', { name: asset.name }))) return;
    await appStore.deleteAsset(asset.id);
    navigate('/');
  };

  // NAVs refresh on their own (each evening's publication is picked up by the poll); this is for the impatient.
  const refreshNav = async () => {
    setRefreshingNav(true);
    try {
      await primeAsset(asset);
      const q = appStore.get().quotes[quoteKey(asset)];
      appStore.notify(q?.source === 'tmx' && !q.stale ? t('detail.navRefreshed', { symbol, nav: formatPrice(q.price), ccy: q.currency, date: formatShortDate(q.asOf) }) : t('detail.navNotFound', { symbol }));
    } finally {
      setRefreshingNav(false);
    }
  };

  return (
    <div className="page">
      <Header crumbs={[{ label: t('header.list'), to: '/' }, { label: portfolio?.name ?? '' }]} />

      <section className={s.top}>
        <div>
          <div className={s.titleRow}>
            <span className={`serif ${s.symbol}`}>{isCash ? t('list.cashName', { ccy: cashCcy }) : symbol}</span>
            {!isCash && <span style={{ fontSize: 16 }}>{profile?.name ?? asset.name}</span>}
            <span className="muted" style={{ fontSize: 13 }}>{[exchangeLabel(exchange) ?? exchange, holdingLabel(asset), profile?.industry].filter(Boolean).join(' · ')}</span>
            {watched && <span className="badge">{t('list.watch')}</span>}
          </div>
          <div className={s.priceRow}>
            <span className={`serif num ${s.price}`}>{isCash ? (v.priceUsd == null ? '—' : `${formatRate(v.priceUsd)} USD`) : formatMoney(last, asset.currency)}</span>
            {!isCash && v.quote?.changePct != null && (
              <span className={cx('num', s.change, v.quote.changePct >= 0 ? 'up' : 'down')}>
                {v.quote.change != null ? `${formatSigned(v.quote.change)} · ` : ''}{formatPct(v.quote.changePct)} {t('common.today')}
              </span>
            )}
            {isCash ? <span className="badge"><i className="dot close" />{t('detail.ecb', { date: app.fx?.date ?? '—' })}</span> : <FreshnessBadge quote={v.quote} withSource fallback={asset.manualPrice ? (asset.manualPriceAt ? t('fresh.manualDated', { date: formatShortDate(asset.manualPriceAt) }) : t('fresh.manual')) : t('fresh.none')} />}
          </div>
        </div>
        <div className={s.actions}>
          {isEquity && (
            <a className="btn quiet" href={googleFinanceUrl(symbol, exchange)} target="_blank" rel="noopener noreferrer">{t('detail.openGoogle')} <IconExternal size={12} /></a>
          )}
          {isFund && (
            <a className="btn quiet" href={mutualFundUrl(symbol, asset.market)} target="_blank" rel="noopener noreferrer">{t('detail.openGoogleFund')} <IconExternal size={12} /></a>
          )}
          {koyfin === 'pending' && !isCash && <span className="btn quiet" aria-disabled="true" style={{ opacity: 0.45 }}>{t('detail.openKoyfin')} <IconExternal size={12} /></span>}
          {typeof koyfin === 'string' && koyfin !== 'pending' && (
            <a className="btn quiet" href={koyfin} target="_blank" rel="noopener noreferrer" title={t('detail.koyfinTitle')}>{t('detail.openKoyfin')} <IconExternal size={12} /></a>
          )}
          {isFund && (
            <button type="button" className="btn quiet" disabled={refreshingNav} onClick={() => void refreshNav()}>{refreshingNav ? t('detail.refreshingNav') : t('detail.refreshNav')}</button>
          )}
          <button type="button" className="btn quiet" onClick={() => setEditing(true)}>{isFund ? t('detail.updateNav') : t('detail.editHolding')}</button>
          <button type="button" className="btn danger" onClick={() => void remove()}>{t('common.delete')}</button>
          {!isCash && <button type="button" className="btn primary" onClick={() => navigate('/experts')}>{t('detail.ask')}</button>}
        </div>
      </section>

      <section className={s.stats}>
        <div className={s.stat}>
          <span className="label">{t('detail.held')}</span>
          <span className={`serif num ${s.statValue}`}>{watched ? <span className="muted">{t('detail.watching')}</span> : <>{formatQty(asset.quantity)} <span className="muted" style={{ fontSize: 13, fontFamily: 'var(--font-sans)' }}>{isCash ? cashCcy : asset.assetClass === 'crypto' ? symbol : isFund ? t('detail.units') : t('detail.shares')}</span></>}</span>
        </div>
        {!isCash && <div className={s.stat}><span className="label">{t('detail.avgCost')}</span><span className={`serif num ${s.statValue}`}>{asset.costPrice != null ? formatMoney(asset.costPrice, asset.costCurrency ?? asset.currency) : '—'}</span></div>}
        <div className={s.stat}><span className="label">{t('detail.value', { ccy })}</span><span className={`serif num ${s.statValue}`}>{watched ? '—' : formatMoney(v.valueUsd == null ? null : convertFromUsd(v.valueUsd, ccy, app.fx), ccy)}</span></div>
        <div className={s.stat}><span className="label">{t('detail.share')}</span><span className={`serif num ${s.statValue}`}>{share == null || watched ? '—' : formatPct(share, false)}</span></div>
        {pnlPct != null && <div className={s.stat}><span className="label">{t('detail.pnl')}</span><span className={cx('serif num', s.statValue, pnlPct >= 0 ? 'up' : 'down')}>{formatPct(pnlPct)}</span></div>}
        {asset.purchaseDate && <div className={s.stat}><span className="label">{t('detail.purchased')}</span><span className={`serif num ${s.statValue}`}>{asset.purchaseDate}</span></div>}
      </section>

      <section className={s.body}>
        <div className={s.chartCol}>
          {watched && <p className="muted" style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.6 }}>{t('detail.watchNote')}</p>}
          {isCash ? (
            <div className={s.cashBox}>
              <p className="muted" style={{ margin: 0, lineHeight: 1.6 }}>{t('detail.cashNote')}{asset.note && <><br />{t('detail.note', { note: asset.note })}</>}</p>
            </div>
          ) : isFund ? (
            <div className={s.cashBox} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p className="muted" style={{ margin: 0, lineHeight: 1.6 }}>{v.quote?.source === 'tmx' ? t('detail.fundTmx', { date: formatShortDate(v.quote.asOf) }) : asset.manualPriceAt ? t('detail.fundNote', { date: formatShortDate(asset.manualPriceAt) }) : t('detail.fundNoDate')}{asset.note && <><br />{t('detail.note', { note: asset.note })}</>}</p>
              {/* The LLM lookup is the fallback for funds TMX Money does not list. */}
              {v.quote?.source !== 'tmx' && <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <NavLookupButton fund={asset} onResult={setNavResult} />
                {navResult && (
                  <>
                    <button type="button" className="btn primary" onClick={() => { void applyNav(asset, navResult).then(() => { appStore.notify(t('nav.applied', { nav: navResult.nav, ccy: navResult.currency ?? asset.currency.toUpperCase(), date: navResult.asOf })); setNavResult(null); }); }}>{t('nav.apply')}</button>
                    <button type="button" className="btn quiet" onClick={() => setNavResult(null)}>{t('common.cancel')}</button>
                  </>
                )}
              </div>}
              {navResult && !navAcceptable(navResult, asset.currency) && <span className="down" style={{ fontSize: 12 }}>{t('nav.check')}</span>}
            </div>
          ) : (
            <div className={s.chart}>
              <TradingViewChart symbol={tvSymbol} className={s.chartFrame} />
              <div className={s.chartNote}>
                <span className="muted">{t('detail.chartNote', { symbol: tvSymbol })}</span>
              </div>
            </div>
          )}
          {asset.note && !isCash && <p className="muted" style={{ marginTop: 14, fontSize: 13 }}>{t('detail.note', { note: asset.note })}</p>}
        </div>

        {isEquity && (
          <section className={s.below}>
            {caListing ? null : mv ? (
              <>
                <div className={s.panel}>
                  <div className={s.sideHead}><span className={`serif ${s.sideTitle}`}>{t('detail.news')}</span><span className="muted" style={{ fontSize: 11 }}>{t('detail.newsSource')}</span></div>
                {sideError && <p className="down" style={{ fontSize: 13 }}>{sideError}</p>}
                {news === null && !sideError && <p className="muted" style={{ fontSize: 13 }}>{t('common.loading')}</p>}
                {news?.length === 0 && <p className="muted" style={{ fontSize: 13 }}>{t('detail.noNews')}</p>}
                {news?.map((n) => (
                  <div key={n.id} className={s.news}>
                    <a href={n.url} target="_blank" rel="noopener noreferrer">{n.title}</a>
                    <span className="muted" style={{ fontSize: 12 }}>{n.publisher} · {formatDateTime(n.publishedAt)}</span>
                  </div>
                ))}
                </div>
                {bars && bars.length > 0 && last != null && (
                  <div className={s.panel}>
                    <div className={s.sideHead}><span className={`serif ${s.sideTitle}`}>{t('detail.history')}</span><span className="muted" style={{ fontSize: 11 }}>{t('detail.historySource')}</span></div>
                    <div className={s.kv}>
                      {PERIODS.map(([key, days]) => {
                        const r = returnSince(bars, days, last);
                        return <Row key={key} label={t(key)} value={r == null ? '—' : formatPct(r)} tone={r == null ? undefined : r >= 0 ? 'up' : 'down'} />;
                      })}
                      {pnlPct != null && <Row label={t('detail.sinceBuy')} value={formatPct(pnlPct)} tone={pnlPct >= 0 ? 'up' : 'down'} />}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className={s.locked}>
                <span className="serif" style={{ fontSize: 16, fontWeight: 500 }}>{t('detail.lockedTitle')}</span>
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: 'var(--ink-soft)' }}>{fh ? t('detail.lockedFinnhub') : t('detail.lockedNoKey')} {t('detail.lockedBody')}</p>
                <div><button type="button" className="btn primary" onClick={() => navigate('/settings')}>{t('detail.lockedCta')}</button></div>
              </div>
            )}

            {metrics && (
              <div className={s.panel}>
                <div className={s.sideHead}><span className={`serif ${s.sideTitle}`}>{t('detail.fundamentals')}</span><span className="muted" style={{ fontSize: 11 }}>Finnhub</span></div>
                <div className={s.kv}>
                  <Row label={t('detail.pe')} value={metrics.pe != null ? formatNumber(metrics.pe, 1) : '—'} />
                  <Row label={t('detail.range52')} value={metrics.low52 != null && metrics.high52 != null ? `${formatPrice(metrics.low52)} – ${formatPrice(metrics.high52)}` : '—'} />
                  <Row label={t('detail.beta')} value={metrics.beta != null ? formatNumber(metrics.beta, 2) : '—'} />
                  <Row label={t('detail.dividend')} value={metrics.dividendYield != null ? formatPct(metrics.dividendYield, false) : '—'} />
                  {profile?.marketCap != null && <Row label={t('detail.mcap')} value={formatCompactMoney(profile.marketCap * 1_000_000, 'USD')} />}
                </div>
              </div>
            )}
          </section>
        )}
      </section>

      {editing && <AddAssetDialog portfolioId={asset.portfolioId} existing={asset} onClose={() => setEditing(false)} />}
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'up' | 'down' }) {
  return (
    <>
      <span className="muted">{label}</span>
      <span className={cx('num', s.kvV, tone)}>{value}</span>
    </>
  );
}
